<script lang="ts" setup>
import { ref, computed } from 'vue';
import FoldersTree from '../FoldersTree.vue';
import type { MovedResources, Scope } from '../../../runtime/services/StorageService';
import StorageService from '../../../runtime/services/StorageService';

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t } = useLocalization();

const props = defineProps<{
    scope: Scope;
    destination?: number;
    expanded?: number[];
}>();

const emit = defineEmits<{
    (e: 'moved', result: MovedResources): void;
}>();

const foldersTreeRef = ref<any>(null);

const resources = ref<FileResource[]>([]);
const modalRef = ref<any>(null);

const loading = ref(false);
const formScope = ref<Scope>('public');
const formDestination = ref<number>(null);
const formExpanded = ref<number[]>([]);

const resourcesIds = computed(() => resources.value.map(el => el.id));

const moveResources = async (): Promise<void> =>
{
    try
    {
        loading.value = true;

        const result: MovedResources = await StorageService.moveResources(formScope.value, foldersTreeRef.value.currentDestination, resources.value);

        if (result.folders.length > 0 || result.files.length > 0)
        {
            $alert.warning($t('[[[Nie wszystkie wybrane elementy zostały przeniesione. Nie posiadasz wymaganych uprawnień lub, w przypadku przenoszenia katalogów, katalog docelowy jest podkatalogiem katalogu źródłowego. Katalogi i pliki których nie udało się przenieść pozostały nadal w zakładce Wybrane.]]]'));
        }
        else
        {
            $alert.success($t('[[[Wybrane elementy zostały przeniesione.]]]'));
        }

        modalRef.value.hide();
        emit('moved', result);
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => $alert.warning(Object.values(ex.data.errors).find(p => true) as any)
        });
    }
    finally
    {
        loading.value = false;
    }
};

const showModal = (items: FileResource[]): void =>
{
    resources.value = items;
    modalRef.value.show();
};

const beforeShow = () =>
{
    const { scope, destination, expanded } = props;

    formScope.value = uniqueScopes.value.length === 1 ? uniqueScopes.value[0] : scope;
    formDestination.value = destination;
    formExpanded.value = expanded.slice();
};

const uniqueScopes = computed<any>(() => [...new Set(resources.value.map(item => item.scope))]);

defineExpose({
    showModal
});
</script>

<template>
    <IdeoModal
        ref="modalRef"
        :title="$t('[[[Przeniesienie wybranych elementów]]]')"
        modal-class="filemanager-modal"
        backdrop-class="filemanager-backdrop"
        hide-footer
        @before-show="beforeShow"
    >
        <p>{{ $t('[[[Wybierz katalog do którego chcesz przenieść wybrane elementy.]]]') }}</p>

        <FoldersTree
            ref="foldersTreeRef"
            v-model:scope="formScope"
            v-model:destination="formDestination"
            v-model:expanded="formExpanded"
            :excluded="resourcesIds"
            root-folder
        />

        <div class="text-end">
            <ActionButton
                variant="light"
                icon="far fa-ban"
                :text="$t('[[[Anuluj]]]')"
                :disabled="loading"
                @click.prevent="modalRef.hide()"
            />

            <ActionButton
                variant="primary"
                icon="far fa-check"
                :text="$t('[[[Przenieś]]]')"
                :loading="loading"
                @click.prevent="moveResources"
            />
        </div>
    </IdeoModal>
</template>
